import { useState } from 'react';
import { Table, Button, Tag, Popconfirm, Alert, message } from 'antd';
import { PlusOutlined, StopOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { provisioningApi } from '../../api/provisioning';
import { AgentKurModal } from './AgentKurModal';
import type { Enrollment, EnrollmentStatus } from '../../types';

const STATUS_COLOR: Record<EnrollmentStatus, string> = {
  pending: 'gold', registered: 'green', revoked: 'default', expired: 'red',
};
const STATUS_LABEL: Record<EnrollmentStatus, string> = {
  pending: 'Bekliyor', registered: 'Kuruldu', revoked: 'İptal', expired: 'Süresi Doldu',
};

export function FirmEnrollmentsPanel({ firmId, tenantId, erpType }: {
  firmId: number; tenantId?: string | null; erpType?: string;
}) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: enrollments = [], isLoading, isError } = useQuery({
    queryKey: ['enrollments', firmId],
    queryFn: () => provisioningApi.list(firmId),
    enabled: !!firmId,
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => provisioningApi.revoke(firmId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments', firmId] });
      message.success('Enrollment iptal edildi');
    },
    onError: () => message.error('İptal edilemedi'),
  });

  const hasTenant = !!tenantId && tenantId.trim().length > 0;

  const columns = [
    { title: 'Oluşturma', dataIndex: 'createdAt', key: 'createdAt',
      render: (d: string) => new Date(d).toLocaleString('tr-TR') },
    { title: 'ERP', dataIndex: 'erpType', key: 'erpType' },
    { title: 'Durum', dataIndex: 'status', key: 'status',
      render: (s: EnrollmentStatus) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{STATUS_LABEL[s] ?? s}</Tag> },
    { title: 'Agent', dataIndex: 'agentId', key: 'agentId',
      render: (a: string | null) => a ? <code style={{ fontSize: 11 }}>{a}</code> : '-' },
    { title: 'Son Kullanma', dataIndex: 'expiresAt', key: 'expiresAt',
      render: (d: string | null) => d ? new Date(d).toLocaleDateString('tr-TR') : 'Süresiz' },
    { title: 'İşlemler', key: 'actions', render: (_: unknown, r: Enrollment) =>
      (r.status === 'pending' || r.status === 'registered') ? (
        <Popconfirm title="Enrollment'ı iptal et?" okText="Evet" cancelText="Hayır"
          onConfirm={() => revokeMutation.mutate(r.id)}>
          <Button type="text" danger icon={<StopOutlined />} />
        </Popconfirm>
      ) : null },
  ];

  return (
    <div>
      {!hasTenant && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="İttihat tenant'ı tanımlı değil"
          description="Agent Kur ile ilk enrollment'ta tenant otomatik oluşturulur (görünen ad: firma adı) ve firmaya kaydedilir." />
      )}
      <Button type="primary" icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)} style={{ marginBottom: 16 }}>
        Agent Kur
      </Button>
      {isError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
          message="Enrollment listesi alınamadı"
          description="İttihat hub'ına ulaşılamıyor olabilir — bu liste 'boş' değil, 'bilinmiyor'. Sayfayı yenileyin veya hub durumunu kontrol edin." />
      )}
      <Table columns={columns} dataSource={enrollments} rowKey="id" loading={isLoading}
        locale={{ emptyText: isError ? 'Liste alınamadı' : 'Henüz enrollment yok' }} />
      <AgentKurModal open={modalOpen} onClose={() => setModalOpen(false)}
        firmId={firmId} tenantId={tenantId ?? null} erpType={erpType ?? 'erp12'} />
    </div>
  );
}
